/**
 * authority.ts — WHO IS ALLOWED INTO THIS CONSOLE, DECIDED IN ONE PLACE AND
 * DECIDED BY DENYING FIRST.
 *
 * ===========================================================================
 * THE PROPERTY
 * ===========================================================================
 * `org_admin` — or any other tenant role, or every tenant role at once — grants
 * NO console access. That is the two-authority ruling (2026-09-07) expressed at
 * the front door of the platform-staff surface: tenant authority and platform
 * authority do not merge, and the place a merge would first become visible to a
 * human is a screen that opened because somebody was an administrator of a
 * customer organization.
 *
 * The server already refuses a tenant session — `authorizePlatform` answers it
 * as an unknown route, and `client.ts` sends `credentials: 'omit'` so the
 * cookie never leaves the browser. This module is the third layer and it is not
 * redundant: without it the CONSOLE has no opinion at all, and "the API would
 * have said no" is not an opinion. A surface that renders its customer list and
 * then discovers the refusal has already told the operator's browser what the
 * control plane looks like.
 *
 * ===========================================================================
 * WHY THE TENANT BRANCH IS FIRST, AND WHY THAT IS A TESTABILITY DECISION
 * ===========================================================================
 * The lane contract's rule: "the only proof a guard works is a fixture where
 * that guard is the sole thing in the way." If the malformed-credential check
 * ran first, a tenant-session fixture with no token would be refused by THAT
 * check, the tenant branch would never execute, and mutating it away would
 * change nothing while every test stayed green — defence in depth lying about
 * itself.
 *
 * So the order is deliberate and the fixture that proves it is deliberate too:
 * `tenant_session` is decided BEFORE anything looks at a token, and the test
 * hands it a session that carries a perfectly well-formed operator id and a
 * perfectly well-formed secret. Nothing else in this function can refuse that
 * input. Remove the branch and the console admits an org_admin.
 *
 * ===========================================================================
 * DEFAULT DENY
 * ===========================================================================
 * The `default` arm is not unreachable defensive padding. `PresentedPrincipal`
 * is a union that will grow — ADR 0015 §2 puts an OIDC client on this origin,
 * and the day a `platform_oidc_session` variant arrives it must be refused by
 * this function until somebody writes its arm, rather than admitted because
 * the switch fell off the end. An `exhaustive` type assertion would delete that
 * protection at runtime, which is the opposite of what is wanted here.
 */
import type { PlatformActor } from './credentials.js';
import { makeActor } from './credentials.js';

/**
 * What the browser is holding when the console is asked to open.
 *
 * `tenant_session` carries an OPTIONAL `systemToken` on purpose. It is not
 * something a tenant session would ever really have — it is there so a fixture
 * can present the hardest case: a customer administrator who has also got hold
 * of a platform secret. If the console admitted that, the refusal below would
 * be decorative.
 */
export type PresentedPrincipal =
  | {
      readonly kind: 'platform_operator_credential';
      readonly operatorId: string;
      readonly systemToken: string;
    }
  | {
      readonly kind: 'tenant_session';
      readonly orgId: string;
      readonly roles: readonly string[];
      readonly systemToken?: string;
      readonly operatorId?: string;
    }
  | { readonly kind: 'none' };

export type AdmissionRefusal =
  | 'no_credential_presented'
  | 'tenant_authority_is_not_platform_authority'
  | 'malformed_operator_credential'
  | 'unknown_principal_kind';

export type Admission =
  | { readonly admitted: true; readonly actor: PlatformActor }
  | {
      readonly admitted: false;
      readonly reason: AdmissionRefusal;
      readonly message: string;
    };

function refuse(reason: AdmissionRefusal, message: string): Admission {
  return { admitted: false, reason, message };
}

export function consoleAdmission(presented: PresentedPrincipal): Admission {
  switch (presented.kind) {
    /**
     * FIRST, AND BEFORE ANY LOOK AT A SECRET. See the docblock: this ordering
     * is what makes the branch provable in isolation.
     */
    case 'tenant_session':
      return refuse(
        'tenant_authority_is_not_platform_authority',
        'This is a customer session. No tenant role — org_admin included — opens the platform ' +
          'console, and holding a platform secret alongside a tenant session does not combine ' +
          'into platform authority. The two authorities do not merge (ruling of 2026-09-07). ' +
          'Nothing about the customer estate is shown on this screen.',
      );

    case 'none':
      return refuse(
        'no_credential_presented',
        'No platform credential has been presented. The console shows nothing until one is.',
      );

    case 'platform_operator_credential': {
      const actor = makeActor(presented.operatorId, presented.systemToken);
      if (!actor) {
        return refuse(
          'malformed_operator_credential',
          'The operator identity or the platform credential is not a usable shape. Nothing was ' +
            'sent to the platform API.',
        );
      }
      return { admitted: true, actor };
    }

    default:
      /**
       * DEFAULT DENY. A principal kind this module has never been taught about
       * is refused, loudly, rather than falling through into the console.
       */
      return refuse(
        'unknown_principal_kind',
        'The console was handed a principal it does not recognise. It is refused rather than ' +
          'admitted: a surface that opens for an unknown kind of caller is not a gate.',
      );
  }
}

/**
 * THE AREAS OF THIS CONSOLE, AND THE PLATFORM CAPABILITY EACH ONE NEEDS.
 *
 * Mirrors `apps/api/src/config/platform-credentials.ts` and is deliberately NOT
 * imported from it — the console is a separate deployable (ADR 0015 §3, sharing
 * is by package and never by process). The console cannot VERIFY which
 * capability a secret holds; only the server can, and it answers 403 with the
 * sentence naming the held capabilities. What this table is for is telling the
 * operator, BEFORE they are refused, which credential an area wants — because
 * `PLATFORM_TENANCY_OPERATORS` and `PLATFORM_PUBLICATION_OPERATORS` are two
 * different secrets and founder ruling 6 says they must stay two.
 */
export const CONSOLE_AREAS = [
  {
    id: 'customers',
    label: 'Customers',
    capability: 'platform:tenancy.administer',
    credentialEnvVar: 'PLATFORM_TENANCY_OPERATORS',
  },
  {
    id: 'knowledge',
    label: 'Global knowledge',
    capability: 'platform:knowledge.publish',
    credentialEnvVar: 'PLATFORM_PUBLICATION_OPERATORS',
  },
] as const;

export type ConsoleAreaId = (typeof CONSOLE_AREAS)[number]['id'];

export function areaFor(id: ConsoleAreaId): (typeof CONSOLE_AREAS)[number] {
  const found = CONSOLE_AREAS.find((a) => a.id === id);
  // Not a `!`: an unknown area id is a programming error and this says so
  // rather than handing back `undefined` for a caller to render as blank.
  if (!found) throw new Error(`unknown console area: ${id}`);
  return found;
}
